/*
 * One visit: Complete (with Undo) and Edit schedule up top, Team and line items with the "this visit or every visit after"
 * question on repeating jobs, notes, photos, the checklist and the timer. A visit that was only computed becomes a row the
 * first time it changes, so the page moves to the new address when the ref it gets back is different.
 */
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  useSettings,
  useReopenVisit,
  useTickChecklist,
  useVisit,
  useVisitContinue,
  useVisitStatus,
  useVisitTimer,
  useOnMyWay,
  useUpload,
} from "../api/hooks";
import type { VisitDetail } from "@steward/shared";
import { useSession } from "../app/session";
import { InputText } from "../components/fields";
import { useBillProgress } from "../api/money";
import { dayWords, shortDate, timeRange, todayIn } from "../components/format";
import { Icon } from "../components/Icon";
import { Menu, Modal, useToast } from "../components/overlays";
import { Banner, Button, Card, Details, IconButton, LoadError, Page, Skeleton, StatusLabel } from "../components/parts";
import { CrewChips, errText, FilesCard, LinesTable, NotesCard, TimerWords, VisitStatus } from "../components/work";
import { DirectionsButton } from "../components/Directions";
import { PhoneRecord } from "../components/PhoneRecord";
import { usePhone } from "../components/usePhone";
import { useYardAsk } from "../components/YardAsk";
import { holdPhoto, worthKeeping } from "../components/photoOutbox";
import { useVisitSheets } from "../components/visitSheets";

export function VisitPage({ refId }: { refId: string }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const visit = useVisit(refId);
  const settings = useSettings(company.id);
  const reopen = useReopenVisit();
  const status = useVisitStatus();
  const timer = useVisitTimer();
  const tick = useTickChecklist();
  const phone = usePhone();
  const [tab, setTab] = useState<"visit" | "details" | "notes">("visit");
  const upload = useUpload();
  const yard = useYardAsk();
  const omw = useOnMyWay();
  const [sheet, setSheet] = useState<"progress" | "continue" | null>(null);
  const carry = useVisitContinue();
  const [carryTo, setCarryTo] = useState("");
  const progress = useBillProgress();
  const [part, setPart] = useState("");
  const v = visit.data;
  const today = todayIn(company.timezone);

  /** After any change: follow the visit to its row address if it just got one. */
  const go = (ref: string) => {
    if (ref !== refId) void navigate({ to: "/visits/$ref", params: { ref }, replace: true });
  };
  const follow = (d: VisitDetail) => go(d.ref);
  /** Edit schedule, Team, Line items, Complete and Apply to future visits, shared with the schedule's visit card. */
  const edit = useVisitSheets(v, go);
  const fail = (e: unknown) => toast(errText(e), { kind: "error" });
  /** Open Camera: the photo goes on the visit, like Photos, Add photos. Lawn asks which yard first (YardAsk). */
  const addPhoto = (f: File) => {
    if (!v) return;
    const r = new FileReader();
    r.onload = () => {
      const data = typeof r.result === "string" ? (r.result.split(",")[1] ?? "") : "";
      yard.ask(1, (room) => {
        const file = {
          name: f.name,
          mime: f.type || "image/jpeg",
          kind: "photo" as const,
          dataBase64: data,
          room: room ?? undefined,
        };
        upload.mutate(
          { on: "visits", id: v.ref, file },
          {
            onSuccess: () => toast("Photo added"),
            onError: (e) => {
              // Kept on this phone to send again, under Photos (photoOutbox.ts).
              if (!worthKeeping(e)) return fail(e);
              holdPhoto(`visits:${v.ref}`, `visits/${v.ref}/photos`, file, e);
              toast("The photo didn't send. It is kept under Photos. Tap Try again.", { kind: "error" });
            },
          },
        );
      });
    };
    r.readAsDataURL(f);
  };

  if (visit.error)
    return (
      <Page title="Visit">
        <LoadError error={visit.error} retry={() => void visit.refetch()} />
      </Page>
    );
  if (!v)
    return (
      <Page title={<span className="skeleton inline wide" />}>
        <Skeleton rows={6} />
      </Page>
    );

  const editable = can("visit.update");
  const movable = can("schedule.update");
  const done = v.status === "done";
  const off = v.status === "skipped" || v.status === "cancelled";
  const setStatus = (to: "skipped" | "cancelled" | "scheduled", words: string) =>
    status.mutate(
      { ref: v.ref, status: to },
      {
        onSuccess: (d) => {
          follow(d);
          toast(words, to === "scheduled" ? {} : { undo: () => status.mutate({ ref: d.ref, status: "scheduled" }) });
        },
        onError: fail,
      },
    );
  const primary = editable ? (
    done ? (
      <Button
        type="secondary"
        icon="undo"
        loading={reopen.isPending}
        onClick={() =>
          reopen.mutate(v.ref, {
            onSuccess: (d) => {
              follow(d);
              toast("Visit reopened");
            },
            onError: fail,
          })
        }
      >
        Reopen Visit
      </Button>
    ) : off ? (
      <Button type="secondary" onClick={() => setStatus("scheduled", "Visit is back on")}>
        Put back on
      </Button>
    ) : (
      <Button icon="check" loading={edit.completing} onClick={edit.markDone}>
        Complete
      </Button>
    )
  ) : null;
  const moreMenu = (
    <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
      {(close) => (
        <>
          <button
            type="button"
            role="menuitem"
            onClick={() => (close(), void navigate({ to: "/jobs/$id", params: { id: v.jobId } }))}
          >
            Open job
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => (close(), void navigate({ to: "/clients/$id", params: { id: v.clientId } }))}
          >
            Open client
          </button>
          {movable && v.repeating && v.status === "scheduled" ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                edit.openFuture();
              }}
            >
              Apply to future visits
            </button>
          ) : null}
          {movable && !off && !v.continuedTo ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                setCarryTo("");
                setSheet("continue");
              }}
            >
              Continue on another day
            </button>
          ) : null}
          {editable && !done && !off ? (
            <>
              {!v.repeating && can("invoice.update") && (settings.data?.settings.invoices.progress ?? true) ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    setPart("");
                    setSheet("progress");
                  }}
                >
                  Bill part now
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  setStatus("skipped", `Skipped ${shortDate(v.date)}`);
                }}
              >
                Skip this visit
              </button>
              <button
                type="button"
                role="menuitem"
                className="danger"
                onClick={() => {
                  close();
                  setStatus("cancelled", `Canceled ${shortDate(v.date)}`);
                }}
              >
                Cancel this visit
              </button>
            </>
          ) : null}
        </>
      )}
    </Menu>
  );
  const banners = (
    <>
      {v.continuedTo ? (
        <Banner
          type="notice"
          action={
            movable ? (
              <Button
                type="secondary"
                size="small"
                loading={carry.isPending}
                onClick={() =>
                  carry.mutate(
                    { ref: v.ref, undo: true },
                    {
                      onSuccess: (d) => {
                        follow(d);
                        toast("The extra day is gone. This visit is open again.");
                      },
                      onError: fail,
                    },
                  )
                }
              >
                Undo
              </Button>
            ) : null
          }
        >
          Done for the day. The job goes on {shortDate(v.continuedTo.date)}, and that day bills it.{" "}
          <a href={`/visits/${v.continuedTo.ref}`}>Open that day</a>
        </Banner>
      ) : null}
      {v.continuedFrom ? (
        <Banner type="notice">
          Part {String(v.part ?? 2)} of this job, carried on from {shortDate(v.continuedFrom.date)}.{" "}
          <a href={`/visits/${v.continuedFrom.ref}`}>Open that day</a>
        </Banner>
      ) : null}
      {done && v.completedAt && !v.continuedTo ? (
        <Banner type="success">
          Done {shortDate(v.completedAt.slice(0, 10))}
          {v.completedBy ? ` by ${v.completedBy}` : ""}
          {v.crewCollected === "company"
            ? ". The client gave the crew a company check."
            : v.crewCollected
              ? `. The client paid the crew by ${v.crewCollected}.`
              : "."}
        </Banner>
      ) : null}
    </>
  );
  const lineCard = (
    <Card
      title="Line items"
      action={
        editable && !done ? (
          <Button type="tertiary" size="small" icon="edit" onClick={() => edit.open("lines")}>
            Change
          </Button>
        ) : null
      }
      flush
    >
      <LinesTable lines={v.lines} footer={<span className="muted">This visit</span>} />
      {v.linesEdited ? <p className="card-sub muted small">Prices on this visit differ from the job.</p> : null}
    </Card>
  );
  const checklistCard = v.checklist ? (
    <Card title={v.checklist.name}>
      <div className="pick-list">
        {v.checklist.left.length && !done ? (
          <p className="muted small">Before the crew can complete it: {v.checklist.left.join(", ")}.</p>
        ) : null}
        {v.checklist.items.map((i) => (
          <label key={`${i.id}:${String(i.copy)}`} className="pick">
            <input
              type="checkbox"
              checked={i.done}
              disabled={!editable || tick.isPending}
              onChange={(e) =>
                tick.mutate(
                  { ref: v.ref, itemId: i.id, done: e.target.checked, copy: i.copy },
                  { onSuccess: follow, onError: fail },
                )
              }
            />
            <span>
              {i.room ? <b>{i.room}: </b> : null}
              {i.label}
              {i.must ? <span className="muted small"> (must do)</span> : null}
              {i.doneBy && i.done ? <span className="muted small"> · {i.doneBy}</span> : null}
            </span>
          </label>
        ))}
      </div>
    </Card>
  ) : null;
  const notesCard = <NotesCard on="visits" id={v.ref} notes={v.notesList} editable={editable} />;
  const photosCard = <FilesCard on="visits" id={v.ref} files={v.photos} editable={editable} title="Photos" />;
  const teamCard = (
    <Card
      title="Team"
      action={
        editable ? (
          <Button type="tertiary" size="small" icon="person" onClick={() => edit.open("team")}>
            Change
          </Button>
        ) : null
      }
    >
      <CrewChips crew={v.crew} />
    </Card>
  );
  const timeCard = (
    <Card title="Time on the job">
      <div className="row between">
        <TimerWords startedAt={v.timerStartedAt} minutes={v.timerMinutes} />
        {editable && !done ? (
          <Button
            type="secondary"
            size="small"
            icon={v.timerStartedAt ? "stop" : "play"}
            loading={timer.isPending}
            onClick={() =>
              timer.mutate({ ref: v.ref, action: v.timerStartedAt ? "stop" : "start" }, { onSuccess: follow, onError: fail })
            }
          >
            {v.timerStartedAt ? "Stop" : "Start"}
          </Button>
        ) : null}
      </div>
    </Card>
  );
  const fromJob =
    v.officeNotes || v.crewNotes ? (
      <Card title="Notes from the job">
        {v.officeNotes ? (
          <p>
            <StatusLabel status="informative">Office</StatusLabel> {v.officeNotes}
          </p>
        ) : null}
        {v.crewNotes ? (
          <p>
            <StatusLabel status="inactive">Crew</StatusLabel> {v.crewNotes}
          </p>
        ) : null}
      </Card>
    ) : null;
  const sheets = (
    <>
      {edit.sheets}
      <Modal
        title="Continue on another day"
        open={sheet === "continue"}
        onClose={() => setSheet(null)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setSheet(null)}>
              Cancel
            </Button>
            <Button
              disabled={!carryTo}
              loading={carry.isPending}
              onClick={() =>
                carry.mutate(
                  { ref: v.ref, to: carryTo },
                  {
                    onSuccess: (d) => {
                      setSheet(null);
                      follow(d);
                      toast(`The job goes on ${shortDate(carryTo)}`);
                    },
                    onError: fail,
                  },
                )
              }
            >
              Add the day
            </Button>
          </>
        }
      >
        <div className="stack">
          <p>
            The job could not be finished today. This day is done for the day and keeps its notes and photos. The new day gets the
            job's price, and the invoice is made when that last day is done.
          </p>
          <InputText
            label="Day it goes on"
            type="date"
            min={v.date}
            value={carryTo}
            onChange={(e) => setCarryTo(e.target.value)}
          />
        </div>
      </Modal>
      <Modal
        title="Bill part now"
        open={sheet === "progress"}
        onClose={() => setSheet(null)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setSheet(null)}>
              Cancel
            </Button>
            <Button
              loading={progress.isPending}
              onClick={() => {
                const cents = Math.round(Number(part.replace(/[$,s]/g, "")) * 100);
                if (!Number.isFinite(cents) || cents < 100) {
                  toast("Type an amount of $1 or more.", { kind: "error" });
                  return;
                }
                progress.mutate(
                  { ref: v.ref, amountCents: cents },
                  {
                    onSuccess: (inv) => {
                      setSheet(null);
                      toast("Progress invoice made. Check it and send it from Invoices.");
                      void navigate({ to: "/invoices/$id", params: { id: inv.id } });
                    },
                    onError: fail,
                  },
                );
              }}
            >
              Make the invoice
            </Button>
          </>
        }
      >
        <div className="stack">
          <p>
            Bill part of this job before it is done. The part goes on its own invoice, and the invoice made when the job is done
            bills the rest. Crew pay counts the whole job once.
          </p>
          <InputText label="Amount" prefix="$" inputMode="decimal" value={part} onChange={(e) => setPart(e.target.value)} />
        </div>
      </Modal>
    </>
  );

  if (phone) {
    // Like Jobber's visit on a phone (owner shots, 2026-10-30): Visit for the client, the job's name, the address with
    // Directions and On my way under it, the day and time, the main button, then Visit, Details and Notes tabs and an
    // Open Camera button over the bottom tabs.
    const where = v.property?.address ?? v.address ?? "";
    const camera = editable ? (
      <label className={`btn primary${upload.isPending ? " busy" : ""}`}>
        <input
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) addPhoto(f);
          }}
        />
        Open Camera
        <Icon name="camera" size={22} />
      </label>
    ) : null;
    return (
      <PhoneRecord
        back={{ label: "Back", go: () => void navigate({ to: "/schedule" }) }}
        kind="truck"
        status={<VisitStatus status={v.status} late={v.date < today} />}
        title={`Visit for ${v.clientName}`}
        sub={<span className="prec-sub-muted">{v.jobName}</span>}
        phone={v.client.phone}
        email={v.client.email}
        facts={[
          ...(where
            ? [
                {
                  key: "where",
                  icon: "directions" as const,
                  main: where,
                  after: (
                    <>
                      <DirectionsButton address={where}>Directions</DirectionsButton>
                      {!done && !off && can("myvisit.update") ? (
                        <Button
                          type="secondary"
                          loading={omw.isPending}
                          onClick={() =>
                            omw.mutate(v.ref, {
                              onSuccess: (r) => toast(`Sent. The client hears you arrive in about ${String(r.minutes)} minutes.`),
                              onError: fail,
                            })
                          }
                        >
                          On my way
                        </Button>
                      ) : null}
                    </>
                  ),
                },
              ]
            : []),
          {
            key: "when",
            icon: "schedule" as const,
            main: (
              <>
                {shortDate(v.date)}, {timeRange(v.startTime, v.endTime)}
              </>
            ),
            ...(v.moved ? { sub: `Moved from ${shortDate(v.originalDate)}` } : {}),
            ...(movable && !done ? { onClick: () => edit.open("schedule") } : {}),
          },
        ]}
        actions={
          <>
            {primary}
            {moreMenu}
          </>
        }
        tabs={{
          tabs: [
            { key: "visit", label: "Visit" },
            { key: "details", label: "Details" },
            { key: "notes", label: "Notes" },
          ],
          active: tab,
          onChange: (k) => setTab(k as typeof tab),
        }}
        fab={camera}
      >
        {yard.sheet}
        {banners}
        {tab === "visit" ? (
          <>
            {lineCard}
            {checklistCard}
            {teamCard}
            {timeCard}
            {fromJob}
            {photosCard}
          </>
        ) : tab === "details" ? (
          <section className="card prec-section">
            <div className="prec-list">
              <button
                type="button"
                className="prec-row"
                onClick={() => void navigate({ to: "/jobs/$id", params: { id: v.jobId } })}
              >
                <span>
                  <b>
                    {v.clientName} - {v.jobName}
                  </b>
                </span>
                <Icon name="arrowRight" size={22} />
              </button>
              <button
                type="button"
                className="prec-row"
                onClick={() => void navigate({ to: "/clients/$id", params: { id: v.clientId } })}
              >
                <Icon name="person" size={22} />
                <span>
                  <b>{v.client.name}</b>
                  {where ? <span className="muted">{where}</span> : null}
                </span>
                <Icon name="chevronRight" size={20} />
              </button>
              <button
                type="button"
                className="prec-row"
                onClick={() => void navigate({ to: "/jobs/$id", params: { id: v.jobId } })}
              >
                <Icon name="hammer" size={22} />
                <span>
                  <b>{v.jobNumber ? `Job #${String(v.jobNumber)}` : "Job"}</b>
                  <span className="muted">{v.repeating ? "Repeats" : "One time"}</span>
                </span>
                <Icon name="chevronRight" size={20} />
              </button>
            </div>
          </section>
        ) : (
          notesCard
        )}
        {sheets}
      </PhoneRecord>
    );
  }

  return (
    <Page
      title={v.clientName}
      titleMetaData={<VisitStatus status={v.status} late={v.date < today} />}
      subtitle={
        <>
          {dayWords(v.date, today)}, {shortDate(v.date)} · {timeRange(v.startTime, v.endTime)}
          {v.moved ? <span className="muted"> · moved from {shortDate(v.originalDate)}</span> : null}
        </>
      }
      primaryAction={primary}
      secondaryAction={
        movable && !done ? (
          <Button type="secondary" icon="schedule" onClick={() => edit.open("schedule")}>
            Edit schedule
          </Button>
        ) : null
      }
      moreActions={moreMenu}
    >
      {banners}
      <div className="split">
        <div className="stack">
          {lineCard}
          {checklistCard}
          {notesCard}
          {photosCard}
        </div>
        <div className="stack">
          {teamCard}
          {timeCard}
          <Card title="Details">
            <Details
              rows={[
                [
                  "Client",
                  <button
                    key="c"
                    type="button"
                    className="link"
                    onClick={() => void navigate({ to: "/clients/$id", params: { id: v.clientId } })}
                  >
                    {v.client.name}
                  </button>,
                ],
                ["Phone", v.client.phone ?? "None"],
                ["Address", v.property?.address ?? v.address ?? "None"],
                ...(v.property?.summary ? [["Home", v.property.summary] as [string, string]] : []),
                [
                  "Job",
                  <button
                    key="j"
                    type="button"
                    className="link"
                    onClick={() => void navigate({ to: "/jobs/$id", params: { id: v.jobId } })}
                  >
                    {v.jobName}
                    {v.jobNumber ? ` #${String(v.jobNumber)}` : ""}
                  </button>,
                ],
                ["Repeats", v.repeating ? "Yes" : "One time"],
              ]}
            />
          </Card>
          {fromJob}
        </div>
      </div>
      {sheets}
    </Page>
  );
}
